import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Sparkles, ArrowRight, Lightbulb, Video, FolderOpen, Mic, FileText, Copy, Star, StarHalf,
  Send, Crown, Trash2, History, Upload, Check, X, Zap, Target, FileCheck, Trophy,
  Users, Shield, TrendingUp, Clock, Activity, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const GLOBE = "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/3d3fc2ab92303e12e5e7ef0aa9bf62ebc24fb8e7c5b9f1d20dd6cdafceedc220.jpeg";
const TEAL = "#1DB87A";

const PROOFS = [
  { key: "Video Introduction", icon: Video, eff: "90% effective" },
  { key: "Portfolio", icon: FolderOpen, eff: "60% effective" },
  { key: "Voice Recording", icon: Mic, eff: "40% effective" },
];

const STAT_ICONS = { users: Users, file: FileText, zap: Zap, clock: Clock, trending: TrendingUp };

function Stars({ value = 0 }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        i < full ? <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
          : (i === full && half) ? <StarHalf key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
            : <Star key={i} className="w-4 h-4 text-white/25" />
      ))}
    </div>
  );
}

/* ============================ PART 1 — LIVE MARKET INSIGHTS ============================ */
function MarketInsights({ m }) {
  if (!m) return null;
  const kFmt = (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v);
  return (
    <section className="relative" data-testid="market-insights">
      {/* glowing globe */}
      <img src={GLOBE} alt="" aria-hidden
        className="pointer-events-none absolute -top-10 right-0 w-56 sm:w-72 lg:w-96 opacity-80 select-none"
        style={{ filter: "drop-shadow(0 0 60px rgba(29,184,122,0.45))", maskImage: "radial-gradient(circle, black 60%, transparent 78%)", WebkitMaskImage: "radial-gradient(circle, black 60%, transparent 78%)" }} />
      <p className="hidden lg:block absolute top-4 right-4 text-[10px] font-semibold tracking-widest text-right leading-5 z-10">
        <span className="text-teal">GLOBAL TALENT.</span> <span className="text-white/70">REAL OPPORTUNITIES.</span><br />
        <span className="text-white/50">A BRIGHTER FUTURE FOR VAs.</span>
      </p>

      <div className="relative z-10 max-w-6xl mx-auto px-4 pt-14">
        <span className="inline-flex items-center gap-2 rounded-full bg-brandgreen/15 border border-brandgreen/30 px-3.5 py-1.5 text-xs font-semibold text-brandgreen" data-testid="live-pill">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brandgreen opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brandgreen" />
          </span>
          Live Market Insights
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 max-w-3xl leading-tight">
          See the Real VA Opportunity <span className="text-teal">on Upwork</span>
        </h2>
        <p className="text-white/60 mt-3 max-w-2xl text-sm sm:text-base">
          Track daily demand, hiring movement, and the countries dominating international freelance delivery.
        </p>

        <div className="grid lg:grid-cols-12 gap-5 mt-8">
          {/* CHART CARD */}
          <div className="lg:col-span-6 rounded-2xl p-5 bg-white/5 border border-white/10 backdrop-blur-xl relative overflow-hidden" data-testid="market-chart">
            <div className="flex items-start justify-between gap-3">
              <p className="font-heading font-bold text-white">Upwork Job Postings & Hires Trend</p>
              <span className="inline-flex items-center gap-1.5 text-xs text-white/70 bg-white/10 rounded-full px-3 py-1.5 border border-white/10">
                {m.chart?.range_label || "Last 30 Days"} <ChevronDown className="w-3.5 h-3.5" />
              </span>
            </div>
            {m.chart?.trend_tooltip && (
              <div className="absolute right-6 top-16 z-20 bg-teal text-white text-[11px] font-bold rounded-lg px-2.5 py-1 shadow-lg shadow-teal/40" data-testid="chart-tooltip">
                {m.chart.trend_tooltip}
              </div>
            )}
            <div className="h-56 mt-4 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={m.chart?.data || []}>
                  <defs>
                    <linearGradient id="tealbar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={TEAL} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={TEAL} stopOpacity={0.15} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis domain={[0, 2500]} ticks={[0, 500, 1000, 1500, 2000, 2500]} tickFormatter={kFmt} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} axisLine={false} tickLine={false} width={34} />
                  <Tooltip contentStyle={{ background: "#0f2038", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="posts" name="Job Posts" fill="url(#tealbar)" radius={[4, 4, 0, 0]} maxBarSize={18} />
                  <Line dataKey="hires" name="Hiring Trend" stroke="#ffffff" strokeWidth={2} dot={{ r: 2, fill: "#60a5fa" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-white/60">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-teal" /> Job Posts</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-400" /> Hires</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-white" /> Hiring Trend</span>
            </div>
          </div>

          {/* STAT CARDS */}
          <div className="lg:col-span-3 space-y-3" data-testid="market-stats">
            {(m.stats || []).map((s, i) => {
              const Icon = STAT_ICONS[s.type] || Activity;
              return (
                <div key={i} className="rounded-2xl p-3.5 bg-white/5 border border-white/10 backdrop-blur-xl flex items-start gap-3" data-testid={`stat-${i}`}>
                  <div className="shrink-0 w-9 h-9 rounded-xl bg-teal/15 text-teal flex items-center justify-center"><Icon className="w-4.5 h-4.5" /></div>
                  <div className="min-w-0">
                    <p className="text-white font-bold leading-tight"><span className="text-lg">{s.value}</span> {s.badge && <span className="text-[10px] align-middle ml-1 bg-brandgreen/20 text-brandgreen px-1.5 py-0.5 rounded-full">{s.badge}</span>}</p>
                    <p className="text-white/55 text-xs leading-tight mt-0.5">{s.label}</p>
                    {s.sub && <p className="text-brandgreen text-[11px] mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {s.sub}</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* COUNTRY RANKING */}
          <div className="lg:col-span-3 rounded-2xl p-5 bg-white/5 border border-white/10 backdrop-blur-xl flex flex-col" data-testid="market-countries">
            <p className="font-heading font-bold text-white text-sm">Top Countries Serving the International Market</p>
            <div className="space-y-2.5 mt-4 flex-1">
              {(m.countries || []).map((c, i) => (
                <div key={i} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${i === 0 ? "bg-teal/15 border-teal/40" : "bg-white/5 border-white/10"}`} data-testid={`country-${i}`}>
                  <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${i === 0 ? "bg-teal text-white" : "bg-white/10 text-white/60"}`}>{i + 1}</span>
                  <span className="text-xl leading-none">{c.flag}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-semibold truncate">{c.name}</p>
                    <p className="text-white/50 text-[11px]">{c.hires}</p>
                  </div>
                  <Activity className={`w-4 h-4 ${i === 0 ? "text-teal" : "text-white/30"}`} />
                </div>
              ))}
            </div>
            <button className="mt-4 w-full rounded-full border border-teal/40 text-teal text-xs font-semibold py-2.5 hover:bg-teal/10 transition-colors flex items-center justify-center gap-1.5" data-testid="country-report-btn">
              See Full Country Report <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ 3D GENERATOR MOCKUP ============================ */
function GeneratorMockup() {
  const checks = ["Job post analyzed", "Client needs identified", "Tailored proposal created", "Optimized for better replies"];
  const badges = ["Clear Structure", "Client-Focused", "Higher Response Rate"];
  return (
    <div className="relative" style={{ perspective: "1400px" }} data-testid="generator-mockup">
      <div className="relative rounded-2xl p-6 bg-gradient-to-br from-[#0f2440] to-[#0a1830] border border-teal/40"
        style={{ transform: "rotateY(-14deg) rotateX(7deg)", boxShadow: "0 30px 80px -20px rgba(29,184,122,0.4), 0 0 0 1px rgba(29,184,122,0.15)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-teal" />
          <span className="text-white/80 text-xs font-semibold">Your Proposal (Ready to Copy)</span>
        </div>
        <div className="space-y-2.5">
          {checks.map((c) => (
            <div key={c} className="flex items-center gap-2.5 text-sm text-white/85">
              <span className="w-5 h-5 rounded-full bg-brandgreen/20 text-brandgreen flex items-center justify-center shrink-0"><Check className="w-3 h-3" /></span>
              {c}
            </div>
          ))}
        </div>
        <button className="mt-5 w-full rounded-xl bg-teal text-white text-sm font-semibold py-2.5 flex items-center justify-center gap-2 shadow-lg shadow-teal/30">
          <Copy className="w-4 h-4" /> Copy to Clipboard
        </button>
      </div>
      {/* floating badges */}
      <div className="absolute -left-5 top-10 hidden sm:flex flex-col gap-2.5">
        {badges.map((b, i) => (
          <span key={b} className="bg-white/10 backdrop-blur-xl border border-white/15 text-white text-[11px] font-semibold rounded-full px-3 py-1.5 shadow-xl"
            style={{ transform: `translateX(${i % 2 ? 12 : -4}px)` }}>{b}</span>
        ))}
      </div>
    </div>
  );
}

/* ============================ UPGRADE / HISTORY / ADMIN ============================ */
function UpgradeDialog({ tier, qrUrl, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("tier", tier);
      fd.append("file", file);
      await api.post("/ai/upgrade-request", fd);
      toast.success("Receipt sent! Coach Reyland will approve your upgrade shortly.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="upgrade-dialog">
        <DialogHeader><DialogTitle>Upgrade to {tier}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Scan the QR to pay via Security Bank or UnionBank, then upload your payment receipt. Coach Reyland approves your upgrade in one tap.</p>
          {qrUrl ? (
            <img src={fileUrl(qrUrl)} alt="Payment QR" className="w-48 h-48 mx-auto rounded-xl border border-border object-contain bg-white" data-testid="upgrade-qr" />
          ) : (
            <div className="w-48 h-48 mx-auto rounded-xl border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground text-center p-4">Payment QR not set yet. Please contact Coach Reyland.</div>
          )}
          <label className={`flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold ${qrUrl ? "bg-teal text-white cursor-pointer hover:bg-teal/90" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`} data-testid="upgrade-upload">
            <Upload className="w-4 h-4" /> {busy ? "Uploading..." : "Upload payment receipt"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={submit} disabled={busy || !qrUrl} />
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryPanel({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/ai/history").then((r) => setRows(r.data)).catch(() => setRows([])); }, [refreshKey]);
  if (!rows) return null;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };
  return (
    <div className="rounded-2xl p-5 bg-white/5 border border-white/10 backdrop-blur-xl mt-6" data-testid="history-panel">
      <p className="font-heading font-bold text-lg flex items-center gap-2 text-white"><History className="w-5 h-5 text-teal" /> Your Recent Proposals</p>
      <p className="text-xs text-white/50 mb-4">Your last 5 generated proposals, ready to reopen and reuse.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-white/50 py-4 text-center">No saved proposals yet. Generate one to see it here.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="border border-white/10 rounded-xl p-3 bg-white/5" data-testid={`history-row-${r.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate text-white">{r.job_preview || "Proposal"}</p>
                  <p className="text-xs text-white/40">{(r.proof_type || "")} · {new Date(r.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full bg-transparent text-white border-white/20 hover:bg-white/10" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`history-view-${r.id}`}>{open === r.id ? "Hide" : "View"}</Button>
                  <Button size="sm" variant="outline" className="rounded-full bg-transparent text-white border-white/20 hover:bg-white/10" onClick={() => copy(r.proposal)} data-testid={`history-copy-${r.id}`}><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              {open === r.id && <div className="bg-black/30 border border-white/10 rounded-xl p-3 mt-3 whitespace-pre-wrap text-sm text-white/80" data-testid={`history-text-${r.id}`}>{r.proposal}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TierRequestsPanel() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/tier-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!rows) return null;
  const approve = async (id) => { await api.post(`/admin/tier-requests/${id}/approve`); toast.success("Upgrade approved"); load(); };
  const decline = async (id) => { await api.post(`/admin/tier-requests/${id}/decline`); toast.success("Request declined"); load(); };
  const del = async (id) => { await api.delete(`/admin/tier-requests/${id}`); load(); };
  const badge = { pending: "bg-amber-100 text-amber-700", approved: "bg-brandgreen/10 text-brandgreen", declined: "bg-slate-100 text-slate-500" };
  return (
    <div className="bg-white border border-border rounded-2xl p-5 shadow-sm mt-6" data-testid="tier-requests-panel">
      <p className="font-heading font-bold text-lg">Tier Upgrade Requests</p>
      <p className="text-xs text-muted-foreground mb-4">Review payment receipts and approve in one tap. Approved upgrades stay active for 30 days.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Student</th><th className="pr-3">Tier</th><th className="pr-3">Receipt</th><th className="pr-3">Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60" data-testid={`tier-req-${r.id}`}>
                <td className="py-2 pr-3"><p className="font-medium">{r.name || "—"}</p><p className="text-xs text-muted-foreground">{r.email}</p></td>
                <td className="pr-3">{r.tier}</td>
                <td className="pr-3"><a href={fileUrl(r.receipt_url)} target="_blank" rel="noreferrer" className="text-teal underline" data-testid={`tier-receipt-${r.id}`}>View</a></td>
                <td className="pr-3"><span className={`text-xs px-2 py-0.5 rounded-full ${badge[r.status] || ""}`}>{r.status}</span></td>
                <td className="pr-3">
                  <div className="flex items-center gap-1.5">
                    {r.status === "pending" && <>
                      <button onClick={() => approve(r.id)} className="text-brandgreen" title="Approve" data-testid={`tier-approve-${r.id}`}><Check className="w-5 h-5" /></button>
                      <button onClick={() => decline(r.id)} className="text-amber-600" title="Decline" data-testid={`tier-decline-${r.id}`}><X className="w-5 h-5" /></button>
                    </>}
                    <button onClick={() => del(r.id)} className="text-red-500" title="Delete" data-testid={`tier-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">No upgrade requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubscriptionsPanel() {
  const [rows, setRows] = useState(null);
  const blank = { name: "", email: "", tier: "Standard", start_date: "", end_date: "", status: "active" };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/admin/subscriptions").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!rows) return null;
  const add = async () => { if (!form.email) return toast.error("Email required"); await api.post("/admin/subscriptions", form); toast.success("Subscription added"); setForm(blank); load(); };
  const del = async (id) => { await api.delete(`/admin/subscriptions/${id}`); load(); };
  return (
    <div className="bg-white border border-border rounded-2xl p-5 shadow-sm mt-6" data-testid="subs-panel">
      <p className="font-heading font-bold text-lg">Subscription Management</p>
      <p className="text-xs text-muted-foreground mb-4">Admin activates subscriptions manually after confirming payment receipt. Automated reminders sent 3 days before expiration via email and push notification.</p>
      <div className="grid sm:grid-cols-6 gap-2 mb-4">
        <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sub-name" />
        <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sub-email" />
        <select className="border border-border rounded-md px-2 text-sm" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="sub-tier">
          <option>Basic</option><option>Standard</option><option>Top Rated</option>
        </select>
        <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="sub-start" />
        <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="sub-end" />
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={add} data-testid="sub-add">Add</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Name</th><th className="pr-3">Email</th><th className="pr-3">Tier</th><th className="pr-3">Start</th><th className="pr-3">End</th><th className="pr-3">Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60" data-testid={`sub-row-${r.id}`}>
                <td className="py-2 pr-3">{r.name}</td><td className="pr-3">{r.email}</td><td className="pr-3">{r.tier}</td>
                <td className="pr-3">{r.start_date}</td><td className="pr-3">{r.end_date}</td>
                <td className="pr-3"><span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "active" ? "bg-brandgreen/10 text-brandgreen" : "bg-slate-100 text-slate-500"}`}>{r.status}</span></td>
                <td><button onClick={() => del(r.id)} className="text-red-500" data-testid={`sub-del-${r.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-muted-foreground">No subscriptions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================ PRICING ============================ */
const FEATURES = [
  { icon: Zap, title: "Submit Faster", desc: "Save hours every week" },
  { icon: Target, title: "Better Positioning", desc: "Stand out with strategy" },
  { icon: FileCheck, title: "Clearer Structure", desc: "Proven 4-paragraph format" },
  { icon: Star, title: "Stronger First Impressions", desc: "Get more client replies" },
];
const TRUST = [
  { icon: Trophy, title: "Proven Framework", desc: "Used by successful VAs" },
  { icon: Users, title: "Real Student Results", desc: "More interviews. More clients." },
  { icon: Shield, title: "Safe and Secure", desc: "Your data stays private" },
];

function PricingCard({ p, onCta }) {
  const isTop = p.name === "Top Rated";
  const isBasic = p.name === "Basic";
  const Icon = isTop ? Crown : isBasic ? Send : Crown;
  return (
    <div data-testid={`pricing-${p.name}`}
      className={`relative rounded-2xl p-6 flex flex-col border overflow-hidden ${isTop ? "bg-gradient-to-br from-[#2a1458] to-[#1a0f3d] border-purple-500" : isBasic ? "bg-gradient-to-br from-teal/25 to-[#0c2236] border-teal/40" : "bg-white/5 border-white/10"}`}
      style={isTop ? { boxShadow: "0 0 50px -8px rgba(168,85,247,0.6), inset 0 0 0 1px rgba(168,85,247,0.4)" } : {}}>
      {p.popular && <span className="absolute top-4 right-4 bg-purple-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">Most Popular</span>}
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isTop ? "bg-purple-500/25 text-purple-300" : isBasic ? "bg-teal/25 text-teal" : "bg-white/10 text-white"}`}><Icon className="w-5 h-5" /></div>
      <p className="font-heading font-bold text-xl text-white mt-3">{p.name}</p>
      <div className="mt-1.5"><Stars value={p.stars ?? (isBasic ? 3.5 : isTop ? 5 : 4)} /></div>
      <p className="text-white/55 text-sm mt-2">{p.per}</p>
      <p className={`font-heading font-extrabold mt-2 ${isBasic ? "text-teal" : "text-white"} text-3xl`}>{p.price}</p>
      <div className="space-y-2 mt-4 flex-1">
        {(p.features || []).map((f, i) => (
          <div key={i} className="flex items-start gap-2 text-sm text-white/80"><Check className="w-4 h-4 text-brandgreen shrink-0 mt-0.5" /> {f}</div>
        ))}
        {p.disclaimer && <div className="flex items-start gap-2 text-sm text-red-300 font-semibold"><X className="w-4 h-4 shrink-0 mt-0.5" /> {p.disclaimer}</div>}
      </div>
      <Button onClick={onCta} data-testid={`cta-${p.name}`}
        className={`rounded-full mt-5 w-full font-semibold ${isTop ? "bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-500/90 hover:to-purple-700/90 text-white shadow-lg shadow-purple-500/40" : isBasic ? "bg-transparent border border-teal text-teal hover:bg-teal/10" : "bg-teal hover:bg-teal/90 text-white"}`}>
        {p.cta || (isBasic ? "Start Free" : `Choose ${p.name}`)} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  );
}

/* ============================ PAGE ============================ */
export default function AIProposalGenerator() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [market, setMarket] = useState(null);
  const [job, setJob] = useState("");
  const [profile, setProfile] = useState("");
  const [proof, setProof] = useState("Video Introduction");
  const [link, setLink] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [upgradeTier, setUpgradeTier] = useState(null);
  const [histKey, setHistKey] = useState(0);
  useEffect(() => { api.get("/home").then((r) => { setCfg(r.data.ai_section); setMarket(r.data.market); }); }, []);
  if (!cfg) return <Loader />;
  const isAdmin = user && ["admin", "reviewer", "support"].includes(user.role);

  const generate = async () => {
    if (!user) { toast.error("Please log in to generate"); nav("/login"); return; }
    if (!job.trim() || !profile.trim()) return toast.error("Paste the job post and your profile details");
    setBusy(true); setOut("");
    try {
      const { data } = await api.post("/ai/proposal", { job_post: job, profile_details: profile, proof_type: proof, proof_link: link });
      setOut(data.proposal);
      setHistKey((k) => k + 1);
      if (data.remaining !== null && data.remaining !== undefined) toast.success(`Generated! ${data.remaining} left this week on ${data.tier}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(out); toast.success("Copied to clipboard"); };
  const scrollToGen = () => document.getElementById("generator")?.scrollIntoView({ behavior: "smooth" });
  const onCta = (p) => {
    if (p.name === "Basic") { if (!user) nav("/register"); else scrollToGen(); return; }
    if (!user) { toast.error("Please log in first"); nav("/login"); return; }
    setUpgradeTier(p.name);
  };

  return (
    <div className="relative min-h-screen bg-[#081322] overflow-hidden" data-testid="ai-proposal-page">
      {/* ambient glows */}
      <div className="pointer-events-none absolute top-0 left-0 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(circle, rgba(29,184,122,0.5), transparent 70%)" }} />
      <div className="pointer-events-none absolute bottom-40 right-0 w-[36rem] h-[36rem] rounded-full blur-3xl opacity-20" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.5), transparent 70%)" }} />

      <MarketInsights m={market} />

      {/* PART 2 — AI PROPOSAL GENERATOR */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 pt-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-white" data-testid="built-pill">
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /> Built for Upwork Freelancers
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white mt-4 leading-tight">
              AI Proposal Generator <span className="text-teal">for Upwork Freelancers</span>
            </h1>
            <p className="text-teal font-semibold mt-3">Trained by the expert — Coach Reyland.</p>
            <p className="text-white/60 mt-2 max-w-lg">Designed to help VAs submit faster with better positioning, clearer structure, and stronger first impressions.</p>
            <Button onClick={scrollToGen} className="rounded-full bg-teal hover:bg-teal/90 text-white mt-6 font-semibold" data-testid="start-generating-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> Start Generating <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
          <div className="pl-6 sm:pl-10"><GeneratorMockup /></div>
        </div>

        {/* feature icons row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl p-5 bg-white/5 border border-white/10 backdrop-blur-xl" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-teal/15 text-teal flex items-center justify-center"><f.icon className="w-5 h-5" /></div>
              <p className="font-heading font-bold text-white mt-3">{f.title}</p>
              <p className="text-white/55 text-sm mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FUNCTIONAL GENERATOR */}
      <section id="generator" className="relative z-10 max-w-6xl mx-auto px-4 pt-20 scroll-mt-20">
        {cfg.pro_tip && (
          <div className="rounded-2xl px-5 py-3 flex items-start sm:items-center gap-3 mb-6 bg-teal/15 border border-teal/30" data-testid="ai-protip">
            <Lightbulb className="w-5 h-5 text-amber-300 shrink-0 mt-0.5 sm:mt-0" />
            <p className="text-sm text-white/80">{cfg.pro_tip}</p>
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* INPUTS */}
          <div className="rounded-2xl p-5 bg-white/5 border border-white/10 backdrop-blur-xl space-y-5" data-testid="ai-inputs">
            <p className="font-heading font-bold text-lg text-white">Your Inputs</p>
            <div>
              <p className="font-semibold text-sm text-white">Job post</p>
              <p className="text-xs text-white/50 mb-1">Paste the full text or a screenshot's text</p>
              <Textarea rows={5} value={job} onChange={(e) => setJob(e.target.value)} className="bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="ai-job" />
            </div>
            <div>
              <p className="font-semibold text-sm text-white">Profile details</p>
              <p className="text-xs text-white/50 mb-1">Paste your profile title and description</p>
              <Textarea rows={4} value={profile} onChange={(e) => setProfile(e.target.value)} className="bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="ai-profile" />
            </div>
            <div>
              <p className="font-semibold text-sm text-white mb-2">Proof type</p>
              <div className="grid grid-cols-3 gap-2">
                {PROOFS.map((p) => (
                  <button key={p.key} onClick={() => setProof(p.key)} data-testid={`ai-proof-${p.key.split(" ")[0].toLowerCase()}`}
                    className={`rounded-xl p-3 border text-center transition-all ${proof === p.key ? "border-teal bg-teal/10 ring-2 ring-teal" : "border-white/15 hover:border-teal/50"}`}>
                    <p.icon className="w-5 h-5 mx-auto text-teal" />
                    <p className="text-[11px] font-semibold mt-1.5 leading-tight text-white">{p.key}</p>
                    <span className="inline-block text-[10px] px-2 py-0.5 rounded-full mt-1.5 bg-white/10 text-white/60">{p.eff}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold text-sm text-white">Proof link</p>
              <p className="text-xs text-white/50 mb-1">Paste your Google Drive or portfolio link, or leave blank to use [PROOF LINK]</p>
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://drive.google.com/..." className="bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="ai-link" />
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-4">
              <p className="text-xs text-white/50">Powered by Coach Reyland's official proposal prompt engine.</p>
              <Button className="rounded-full bg-teal hover:bg-teal/90 shrink-0 text-white" onClick={generate} disabled={busy} data-testid="ai-generate">
                <Star className="w-4 h-4 mr-1.5" /> {busy ? "Generating..." : "Generate Proposal"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          {/* PREVIEW */}
          <div className="rounded-2xl p-5 bg-white/5 border border-white/10 backdrop-blur-xl flex flex-col" data-testid="ai-preview">
            <div className="flex items-center justify-between">
              <p className="font-heading font-bold text-lg flex items-center gap-2 text-white"><FileText className="w-5 h-5 text-teal" /> Generated Proposal Preview</p>
              <Button size="sm" variant="outline" className="rounded-full bg-transparent text-white border-white/20 hover:bg-white/10" onClick={copy} disabled={!out} data-testid="ai-copy"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
            </div>
            <div className="bg-black/30 border border-white/10 rounded-xl p-4 mt-4 flex-1 min-h-[300px] whitespace-pre-wrap text-sm text-white/85" data-testid="ai-output">
              {out || <span className="text-white/40">Your client-ready proposal will appear here in four clean paragraphs.</span>}
            </div>
            <p className="text-xs text-white/50 mt-2">Output follows Coach Reyland's 4-paragraph format.</p>
          </div>
        </div>

        {user && !isAdmin && <HistoryPanel refreshKey={histKey} />}
      </section>

      {/* PRICING */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 pt-20">
        <div className="grid md:grid-cols-3 gap-5" data-testid="ai-pricing">
          {(cfg.pricing || []).map((p, i) => <PricingCard key={i} p={p} onCta={() => onCta(p)} />)}
        </div>

        {/* trust bar */}
        <div className="grid sm:grid-cols-3 gap-4 mt-10" data-testid="trust-bar">
          {TRUST.map((t) => (
            <div key={t.title} className="rounded-2xl p-5 bg-white/5 border border-white/10 flex items-center gap-4" data-testid={`trust-${t.title.split(" ")[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-teal/15 text-teal flex items-center justify-center shrink-0"><t.icon className="w-5 h-5" /></div>
              <div><p className="font-heading font-bold text-white text-sm">{t.title}</p><p className="text-white/55 text-xs mt-0.5">{t.desc}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* ADMIN PANELS */}
      {isAdmin && (
        <section className="relative z-10 max-w-6xl mx-auto px-4 pt-16 pb-16">
          <TierRequestsPanel />
          <SubscriptionsPanel />
        </section>
      )}
      {!isAdmin && <div className="pb-20" />}

      {upgradeTier && <UpgradeDialog tier={upgradeTier} qrUrl={cfg.qr_url} onClose={() => setUpgradeTier(null)} onDone={() => setUpgradeTier(null)} />}
    </div>
  );
}
